import React, {PropsWithChildren} from "react";
import {ApiError} from "@connox/typescript-library";
import {BraintreeError} from "braintree-web";

type BoundaryErrorType = ApiError | BraintreeError | Error | Record<string, unknown>;
export type HandleErrorType = (
  err: BoundaryErrorType,
  occurrence: string,
  additional?: Record<string, unknown>,
) => void;
type ErrorBoundaryPropsType = {
  fallback: React.ReactNode;
  handleError?: HandleErrorType;
  occurrence: string;
};

/**
 * Old school error boundary class component. Necessary since there are no reasonable hooks in `React.FC` that could
 * catch that react-ripping, dom-destroying type of error, these `BraintreePayPalButtons` are able to throw. <br/>
 * [Some deep dive](https://www.developerway.com/posts/how-to-handle-errors-in-react)
 */
export class ErrorBoundary extends React.Component<PropsWithChildren<ErrorBoundaryPropsType>, {hasError: boolean}> {
  constructor(props: PropsWithChildren<ErrorBoundaryPropsType>) {
    super(props);
    this.state = {hasError: false};
  }

  static getDerivedStateFromError(error) {
    return {hasError: true};
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (__DEVELOPMENT__) {
      /* eslint-disable no-console */
      console.log("DEVELOPMENT:");
      console.log(error);
      console.log(errorInfo);
      /* eslint-enable no-console */
    }
    if (this.props.handleError) {
      error.name = this.props.occurrence.split(" ")[0] + "Boundary" + error.name;
      this.props.handleError(error, this.props.occurrence, {...errorInfo});
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
